'use client'

import type { ChildrenProps } from '@snowball-tech/common/types'

import useTranslations from '@snowball-tech/common/hooks/useTranslations'
import { Paper } from '@snowball-tech/fractal/dist/src/components/Paper'
import { Tag } from '@snowball-tech/fractal/dist/src/components/Tag'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { cj, cn } from '@snowball-tech/fractal/dist/src/styles/helpers'

import { type ReactNode, createElement, useState } from 'react'

import isEmpty from 'lodash/fp/isEmpty'
import isNumber from 'lodash/fp/isNumber'

type Props = {
  prices: {
    monthly?: number
    quarterly?: number
    semestrally?: number
    yearly?: number
  }
  className?: string
  condensed?: boolean
  description?: ReactNode
  discounts?: {
    monthly?: number
    quarterly?: number
    semestrally?: number
    unit?: 'month' | 'percent' | 'raw'
    yearly?: number
  }
  durationTag?: string
  elevation?: '0' | '1' | '2' | '3'
  monthly?: boolean
  name?: string
  quarterly?: boolean
  selected?:
    | 'largest'
    | 'monthly'
    | 'quarterly'
    | 'semestrally'
    | 'smallest'
    | 'yearly'
  semestrally?: boolean
  showPeriod?: {
    monthly?: boolean
    quarterly?: boolean
    semestrally?: boolean
    yearly?: boolean
  }
  showTotal?: boolean
  subtitle?: string
  yearly?: boolean
  oneShot?: {
    monthly?: boolean
    quarterly?: boolean
    semestrally?: boolean
    yearly?: boolean
  }
  onPlanChange?: (
    plan: 'monthly' | 'quarterly' | 'semestrally' | 'yearly',
  ) => void
} & Partial<ChildrenProps>
export default function Plan({
  children,
  className,
  condensed = false,
  description,
  discounts,
  durationTag,
  elevation = '2',
  monthly = false,
  name,
  oneShot = {},
  onPlanChange,
  prices,
  quarterly = false,
  selected = 'smallest',
  semestrally = false,
  showPeriod = {},
  showTotal,
  subtitle,
  yearly = true,
}: Props) {
  const $t = useTranslations()

  const labels: Array<'monthly' | 'quarterly' | 'semestrally' | 'yearly'> = []
  if (monthly) {
    labels.push('monthly')
  }
  if (quarterly) {
    labels.push('quarterly')
  }
  if (semestrally) {
    labels.push('semestrally')
  }
  if (yearly) {
    labels.push('yearly')
  }

  const [duration, setDuration] = useState<(typeof labels)[number]>(
    (selected === 'smallest'
      ? labels.at(0)
      : selected === 'largest'
        ? labels.at(-1)
        : selected) ??
      (monthly
        ? 'monthly'
        : quarterly
          ? 'quarterly'
          : semestrally
            ? 'semestrally'
            : 'yearly'),
  )

  const handlePlanChange = (plan: (typeof labels)[number]) => {
    setDuration(plan)

    onPlanChange?.(plan)
  }

  const content = (
    <>
      <div
        className={cj(
          'flex flex-col items-center',
          condensed ? 'gap-4' : 'gap-5',
        )}
      >
        {(!isEmpty(name) || !isEmpty(subtitle)) && (
          <div className="flex flex-col items-center gap-1">
            {!isEmpty(name) && (
              <Typography
                className="text-center"
                element="p"
                variant="heading-3"
              >
                {name}
              </Typography>
            )}

            {!isEmpty(subtitle) && (
              <Typography className="text-center" variant="body-2-median">
                {subtitle}
              </Typography>
            )}
          </div>
        )}

        <div className="flex flex-row gap-1">
          {labels.length === 1 && !isEmpty(durationTag) ? (
            <Tag color="white">{durationTag}</Tag>
          ) : (
            labels.map((label) => (
              <Tag
                key={label}
                className={cj(
                  'cursor-pointer whitespace-nowrap',
                  duration === label ? 'bg-dark text-light' : '',
                )}
                color={duration === label ? 'pink' : 'white'}
                role="button"
                tabIndex={-1}
                onClick={() => handlePlanChange(label)}
              >
                {$t.rich(
                  isNumber(discounts?.[label])
                    ? `home.plans.duration.${label}.discounted`
                    : `home.plans.duration.${label}.normal`,
                  {
                    discount: discounts?.[label] ?? 0,
                    unit: discounts?.unit ?? 'percent',
                    yellow: (translationContent: ReactNode) =>
                      createElement(
                        'span',
                        {
                          className:
                            duration === label
                              ? 'text-decorative-yellow-50'
                              : 'text-decorative-purple-50',
                        },
                        translationContent,
                      ),
                  },
                )}
              </Tag>
            ))
          )}
        </div>

        <div className="flex flex-col items-center gap-2">
          <div className="flex flex-col items-center">
            <Typography element="p" variant="heading-1">
              {$t.rich(
                (yearly && !monthly && !quarterly && !semestrally) ||
                  oneShot?.[duration]
                  ? showPeriod?.[duration]
                    ? `home.plans.price.one_shot.${duration}`
                    : 'home.plans.price.one_shot.simple'
                  : 'home.plans.price.monthly',
                {
                  amount: prices[duration] ?? 0,
                  smaller: (translationContent: ReactNode) =>
                    createElement(
                      Typography,
                      {
                        element: 'span',
                        variant: 'body-2-bold',
                      },
                      translationContent,
                    ),
                },
              )}
            </Typography>
          </div>

          {duration !== 'monthly' && !oneShot?.[duration] && showTotal && (
            <Typography variant="body-2-bold">
              {$t('home.plans.price.total', {
                amount: Math.round(
                  (prices?.[duration] ?? 0) *
                    (duration === 'yearly'
                      ? 12
                      : duration === 'semestrally'
                        ? 6
                        : duration === 'quarterly'
                          ? 3
                          : 1),
                ),
              })}
            </Typography>
          )}
        </div>
      </div>

      {children}

      {!isEmpty(description) && (
        <Typography className="w-full" element="div" variant="body-2">
          {description}
        </Typography>
      )}
    </>
  )

  const classNames = cn(
    'flex h-fit w-full flex-1 flex-col items-center gap-5 lg:max-w-[460px]',
    className,
  )

  return elevation === '0' ? (
    <div className={classNames}>{content}</div>
  ) : (
    <Paper className={classNames} elevation={elevation}>
      {content}
    </Paper>
  )
}
